import { useEffect, useRef, useState } from 'react';
import communicationApi from '../../../../../../services/communicationApi';

export interface DraftPreviewFragment {
    key: string;
    content: string;
}

interface DraftPreviewResult {
    rendered: Record<string, string>;
    error: string | null;
    loading: boolean;
}

/**
 * Renderiza no servidor (endpoint stateless) os fragmentos do rascunho em edição e devolve o
 * texto por chave. Substitui o antigo render Jinja feito no cliente (jinjaLite): o estúdio não
 * resolve mais {{ }} no browser; quem renderiza é o backend, com o mesmo renderer do envio.
 */
export const useDraftPreview = (
    fragments: DraftPreviewFragment[],
    values: Record<string, string>,
    environment: string,
): DraftPreviewResult => {
    const [rendered, setRendered] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const signature = JSON.stringify({ fragments, values, environment });
    const latest = useRef(0);
    const keysSignature = fragments.map((f) => f.key).join('|');
    const previousKeys = useRef(keysSignature);

    useEffect(() => {
        const requestId = latest.current + 1;
        latest.current = requestId;
        const templateChanged = previousKeys.current !== keysSignature;
        previousKeys.current = keysSignature;
        if (fragments.length === 0) {
            setRendered({});
            setError(null);
            setLoading(false);
            return undefined;
        }
        if (templateChanged) setRendered({});
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const renders = await communicationApi.previewDraft({
                    templates: fragments.map((f) => ({ role: f.key, content: f.content })),
                    values,
                    environment,
                });
                if (latest.current !== requestId) return;
                const map: Record<string, string> = {};
                renders.forEach((r) => {
                    map[r.role] = r.rendered;
                });
                setRendered(map);
                setError(null);
            } catch (e) {
                if (latest.current !== requestId) return;
                setError(e instanceof Error ? e.message : 'Falha ao renderizar a prévia.');
            } finally {
                if (latest.current === requestId) setLoading(false);
            }
        }, 300);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    return { rendered, error, loading };
};
